import classNames from "classnames";
import CtaPrimary from "components/CtaPrimary";
import NftImage from "components/images/NftImage";
import { BasicProps } from "components/types/BasicProps";
import { INft } from "logic/types/nft/INft";
import { FC, useEffect, useRef, useState } from "react";
import { accountLiterals } from "../literals";
import CtaSecondary from "components/CtaSecondary";
import { useRouter } from "next/navigation";
import useAlertNotification from "components/hooks/useAlertNotification";
import useModalHelper from "components/modals/hooks/useModalHelper";
import { ModalWithdrawNft } from "./ModalWithdrawNft";
import { NftInfoPanel } from "./NftInfoPanel";
import { AiOutlineInfoCircle } from "react-icons/ai";
import {
  MdOutlineCheckBox,
  MdOutlineCheckBoxOutlineBlank,
} from "react-icons/md";

type Props = BasicProps & {
  nft: INft;
  selectionActive: boolean;
  isSelected?: boolean;
  onSelected?: (nft: INft) => void;
  onWithdrawn?: (nft: INft) => void;
};

const {
  borrowCtaEnabled,
  borrowCtaDisabled,
  withdrawCta,
  borrowCtaNotValuated,
} = accountLiterals.uAccountNftCard;

export const UAccountNftCard: FC<Props> = (props) => {
  const {
    nft,
    isSelected,
    onSelected,
    className,
    selectionActive,
    onWithdrawn,
  } = props;
  const [, openAlertNotification] = useAlertNotification();
  const router = useRouter();
  const [isOpenModalWithdraw, toggleModalWithdraw] = useModalHelper();
  const [popoverPosition, setPopoverPosition] = useState<"right" | "left">(
    "right"
  );
  const infoRef = useRef<HTMLDivElement>(null);

  const ctaPrimaryLiteral = nft.isDeposited
    ? borrowCtaDisabled
    : nft.notValuated
    ? borrowCtaNotValuated
    : borrowCtaEnabled;

  const isCtaPrimaryDisabled = nft.isDeposited || nft.notValuated;

  const isCtaSecondaryDisabled = nft.isDeposited;

  const disabled = selectionActive && (nft.isDeposited || nft.notValuated);

  useEffect(() => {
    const handlePopoverPosition = () => {
      if (!infoRef.current) return;

      const parentRect = infoRef.current.getBoundingClientRect();
      const windowWidth =
        window.innerWidth || document.documentElement.clientWidth;
      const threshold = 500;

      if (windowWidth - parentRect.right < threshold) {
        setPopoverPosition("left");
      } else {
        setPopoverPosition("right");
      }
    };

    handlePopoverPosition();

    window.addEventListener("resize", handlePopoverPosition);

    return () => {
      window.removeEventListener("resize", handlePopoverPosition);
    };
  }, []);

  const handleBorrowClick = () => {
    router.push(`/borrow/create-loan?nft=${nft.nftId}`);
  };

  const handleClick = () => {
    if (selectionActive) {
      if (disabled) {
        if (nft.notValuated) {
          openAlertNotification(
            "error",
            "No Pricing Data available for this asset",
            50000
          );
        } else {
          openAlertNotification(
            "error",
            "This NFT is already deposited in a loan",
            50000
          );
        }
      } else {
        onSelected?.(nft);
      }
    }
  };

  return (
    <>
      <label
        className={classNames(
          "w-fit h-fit px-2.5 pt-6 pb-8 rounded-[20px] flex flex-col transition-all",
          className,
          isSelected && selectionActive
            ? "outline-uyellow outline outline-2"
            : "outline-primary outline outline-1",
          disabled && "opacity-50",
          selectionActive && "cursor-pointer"
        )}
        htmlFor={nft.alt}
      >
        <div className="flex flex-col gap-[5px]">
          <div className="relative flex items-center justify-center gap-2.5">
            <div className="-mr-[37px] pr-[37px] w-[245px] flex gap-1 justify-center">
              <h4 className="overflow-hidden whitespace-nowrap text-ellipsis text-sm font-bold">
                {nft.name}
              </h4>
              <span className="border border-uyellow rounded-[9px] px-1.5 font-medium text-xs leading-none flex items-center">
                #{nft.tokenId}
              </span>
            </div>
            <div
              className="absolute right-3 top-0 h-full group flex items-center justify-center"
              ref={infoRef}
            >
              {selectionActive ? (
                isSelected ? (
                  <MdOutlineCheckBox className="w-[18px] h-[18px] text-uyellow" />
                ) : (
                  <MdOutlineCheckBoxOutlineBlank className="w-[18px] h-[18px] text-uyellow" />
                )
              ) : (
                <>
                  <AiOutlineInfoCircle className="w-[18px] h-[18px] text-uyellow" />
                  <NftInfoPanel
                    className={classNames(
                      "hidden group-hover:flex absolute top-3 z-50 w-[403px]",
                      popoverPosition === "left" ? "right-0" : "left-0"
                    )}
                    nft={nft}
                  />
                </>
              )}
            </div>
          </div>
          <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-uyellow to-transparent"></div>
        </div>
        <div className="mt-[14px] w-full relative">
          <input
            className="absolute top-1 right-1 z-50 w-3 h-3 border-2 border-white outline outline-black
            rounded-full checked:bg-primary bg-white hidden"
            type="checkbox"
            checked={isSelected}
            onChange={handleClick}
            id={nft.alt}
            name={nft.alt}
            value={nft.alt}
          />
          <NftImage
            className="h-[276px] w-[276px] rounded-[10px]"
            src={nft.image}
            alt={nft.alt}
          />
        </div>
        {!selectionActive && (
          <div className="mt-[30px] flex flex-col items-center gap-[15px]">
            <CtaPrimary
              className="w-[235px]"
              onClick={handleBorrowClick}
              disabled={isCtaPrimaryDisabled}
            >
              {ctaPrimaryLiteral}
            </CtaPrimary>
            <CtaSecondary
              className="w-[235px]"
              disabled={isCtaSecondaryDisabled}
              onClick={() => toggleModalWithdraw()}
            >
              {withdrawCta}
            </CtaSecondary>
          </div>
        )}
      </label>
      {isOpenModalWithdraw && (
        <ModalWithdrawNft
          isOpen={isOpenModalWithdraw}
          toggleModal={toggleModalWithdraw}
          nft={nft}
          onNftWithdrawn={onWithdrawn}
        />
      )}
    </>
  );
};
